// Script editor query + mutation hooks (plan U7).
//
// Centralises the React Query keys and the regenerate-mutation cache update
// so the editor route stays focused on rendering. Co-located with useSettings
// so a future "all editor hooks live here" pattern can grow without churn.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ContiResponse, RenderStatus, ScriptResponse, SectionName } from '@/api/types'

/** Stable cache key factory for a single script. */
export const scriptKey = (id: number) => ['script', id] as const

/**
 * useScript — fetch a script envelope (project + payload) by id.
 *
 * `enabled` lets the route gate the query while the URL param is still being
 * parsed; the editor passes `enabled={Number.isFinite(id) && id > 0}`.
 */
export function useScript(id: number, enabled = true) {
  return useQuery<ScriptResponse>({
    queryKey: scriptKey(id),
    queryFn: () => api.getScript(id),
    enabled,
  })
}

/**
 * useRegenerateSection — POST a section regen and write the response straight
 * into the script cache so the UI reflects the new payload without a re-GET.
 */
export function useRegenerateSection(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (section: SectionName) => api.regenerateSection(id, section),
    onSuccess: (data) => {
      qc.setQueryData<ScriptResponse>(scriptKey(id), data)
    },
  })
}

/** Stable cache key for a project's storyboard. */
export const contiKey = (id: number) => ['conti', id] as const

/**
 * useConti — fetch a project's stored ContiPackage. `payload` is undefined
 * until the project has been decomposed at least once.
 */
export function useConti(id: number, enabled = true) {
  return useQuery<ContiResponse>({
    queryKey: contiKey(id),
    queryFn: () => api.getConti(id),
    enabled,
  })
}

/**
 * useDecompose — run the decompose phase and write the fresh storyboard into
 * the conti cache so the board re-renders without a re-GET.
 */
export function useDecompose(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.decomposeScript(id),
    onSuccess: (data) => {
      qc.setQueryData<ContiResponse>(contiKey(id), data)
    },
  })
}

export const renderStatusKey = (id: number) => ['render-status', id] as const

export function useRender(id: number, enabled = true) {
  const qc = useQueryClient()
  const status = useQuery<RenderStatus>({
    queryKey: renderStatusKey(id),
    queryFn: () => api.renderStatus(id),
    enabled,
    retry: false,
    refetchInterval: (query) => (query.state.data?.state === 'running' ? 2000 : false),
  })
  const start = useMutation({
    mutationFn: () => api.startRender(id),
    onSuccess: (data) => {
      qc.setQueryData<RenderStatus>(renderStatusKey(id), data)
    },
  })
  return { status, start }
}
